/* eslint-disable @typescript-eslint/no-explicit-any */

import { AuthenticateUserResp, SignIn } from "@/pages/Authentication/AuthUtils/Login";
import { QueryClient, useMutation } from "@tanstack/react-query";
import { CognitoRefreshToken, CognitoUser, CognitoUserSession } from "amazon-cognito-identity-js";
import { executeWithin10Minutes } from "./SessionTime";
import { signUp } from "@/pages/Authentication/AuthUtils/Signup";
import { usePostUserHiringresource } from '../../../openapi/api/endpoints/default/default';
import { AddClientRedirect } from '@/pages/Authentication/AuthComponents/SignUp';
import { HrUser } from '../../../openapi/api/model/hrUser';
import useOnAuth from "@/customMutators/useOnAuth";
import { useState } from "react";
/**
 * @Session How it works =>
 * for a given parent and child route, if there is a match on a child route, the beforeLoad for 
 * both the parent AND the child routes will fire (parent then child's) THEN load fires for
 * both in that sequence
 * @usage Use beforeLoad of parent component to validate authentication, then the child 
 * beforeLoad to manage accessToken lifecycle (ensure API calls do not bounce), then parent load 
 * to relevant queryData then childs - this also promotes better separation of concerns
 * @param queryClient instance 
 */


/**
 * @Usage Cognito login
 */
export const useSignIn=({queryClient,onSuccess}:{queryClient:QueryClient,onSuccess?:()=>void})=>{
    return useMutation({
        mutationFn:SignIn,
        mutationKey:['Auth'],
        onSuccess:(data)=>{
            queryClient.setQueryData(['Auth'],data);
            onSuccess&&onSuccess()
        },
    })
}

/**
 * congito login + auth routine
 * @returns 
 */
export const useLoginAuthRoutine=({queryClient, onSuccess}:{queryClient:QueryClient,onSuccess?:()=>void})=>{
    const {hrRefetch:triggerAuthRoutine}=useOnAuth({onAuthSuccess:()=>{}})
    const [error, setError] = useState<undefined | Error>(undefined)
    const {mutateAsync:logIn,isPending}=useSignIn({queryClient, onSuccess})
    const logInAndAuthRoutine=async({userName,password}:{userName:string,password:string})=>{
        try{
            const data=await logIn({userName,password})
            await triggerAuthRoutine({sessionData:{
                token:data.session?.getIdToken().getJwtToken() as string,
                permission:undefined,
                user:data.cognitoUser.getUsername()
            }})
        }catch(err){
            console.log('loginAndAuthRoutineErr',err)
            setError(err as Error)
            throw (err)
        }
   }

   return {logInAndAuthRoutine,isPending,error}







}
/**
 * Account creation logic for when user is added through sharable link. 
 * Normal SignIn does not require this as without a link, we create dev users on the purchase of a license
 * @1-Logs in in cognito and sets access token in cache
 * @2-Create hrUser and optimistic update
 * @3-Auth Routine (fetches hrUser -> gets permission header -> sets it in cache)
 */
export const usePostSignup=({queryClient,onSuccess}:{queryClient:QueryClient,redirect?:AddClientRedirect,HrUser?:HrUser,onSuccess:()=>void})=>{
    const {mutateAsync:createHrUserDB,isPending,error,data:newUserId}=usePostUserHiringresource({
        mutation: {
            onSettled:()=>{
                queryClient.invalidateQueries({queryKey: [`/user-hiringresource`]})
            },
            onMutate:async ({data})=>{//optimistic update
                await queryClient.cancelQueries({ queryKey: [`/user-hiringresource`] })
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                queryClient.setQueryData([`/user-hiringresource`], (_:null) => data as HrUser);
            },
        }
    })
    const {hrRefetch:triggerAuthRoutine}=useOnAuth({onAuthSuccess:()=>{}})
    const {mutateAsync:signIn,isPending:pendingSignIn,isSuccess:signedIn,error:signInError}=useSignIn({
        queryClient,
        onSuccess:()=>{}})
    
    const postSignupRoutine=async({hrUser,password}:{hrUser:HrUser,password:string})=>{
        try{
            const data=await signIn({userName:hrUser.userName,password})
            await createHrUserDB({data:hrUser})
            await new Promise(resolve => setTimeout(resolve, 3000)); //eventually consitency quality of db; gotta delay
            await triggerAuthRoutine({sessionData:{
                token:data.session?.getIdToken().getJwtToken() as string,
                permission:undefined,
                user:data.cognitoUser.getUsername()
            }})
            onSuccess&&onSuccess()
        }catch(err){
            console.log('PostSignupRoutineErr',err)
            throw(err)
        }
    }
    return {
        postSignupRoutine,
        loading:isPending && pendingSignIn,
        isSuccess: signedIn,
        errors:error || signInError ? {...error,...signInError}:null,
        newUserId
    }
}

export const renewToken=({cognitoUser,refreshToken}:{cognitoUser:CognitoUser,refreshToken:CognitoRefreshToken}):Promise<Error | CognitoUserSession>=>{
    return new Promise((resolve, reject) => {
        cognitoUser.refreshSession(refreshToken, (err, session:CognitoUserSession) => {
          if (err) {
            reject(new Error('failed to refresh session'));
          } else {
            resolve(session);
          }
        });
      });
}

/**
 * Checks and refreshes token if needed to maintain session data based on signIn response
 * payload
 * @param AuthPayload signIn response payload
 * @todo Refresh token will eventually expire; if it does, sign the user out
 */
export const manageAccessToken=async ({AuthPayload}:{AuthPayload:AuthenticateUserResp})=>{
    const {session}=AuthPayload
    if (!session){
        throw Error('session data is missing')
    }
    const expirationTime=session.getAccessToken().getExpiration()
    const refreshToken=session.getRefreshToken()
    try{
        const newSessionData=await executeWithin10Minutes({
            callBack: async ()=> renewToken({
                cognitoUser:AuthPayload.cognitoUser,
                refreshToken:refreshToken
            }),
            deadLine:expirationTime
        }) 
        return newSessionData as CognitoUserSession | undefined
    }catch(e){
        throw Error ('Error renewing token')
    }}

type useSignUpProps={
    onFailureFunc?:(err: Error)=>void
}
    
export const useSignUp=(onFailure?:useSignUpProps)=>{
    return useMutation({
        mutationFn:signUp,
        onError:(err)=>{
            if(!onFailure || !onFailure.onFailureFunc)return
            onFailure.onFailureFunc(err)
            console.log({err})
        }
    })
}


